import { memo, useEffect, useState } from "react";
import { TextStyle, View } from "react-native";
import Animated, {
	Easing,
	useAnimatedStyle,
	useSharedValue,
	withRepeat,
	withTiming,
} from "react-native-reanimated";
import isWeb from "src/constants/isWeb";
import { paddingLarge, paddingMedium } from "src/styles/spacing";
import { Text } from "src/swsh-native";
import { MINI_CARD_WIDTH } from "../constants";
import { MiniCardProps } from "../types";

const MiniCardTitle = ({ title }: Pick<MiniCardProps, "title">) => {
	const [forceLongTitle, setForceLongTitle] = useState(false);
	if (!title) return null;
	if (title.length > 10 || forceLongTitle) {
		return <MiniCardLongTitle title={title} />;
	}
	return (
		<Text
			xs
			semibold
			// Fallback because different characters have different widths
			onLayout={(event) => {
				const { width } = event.nativeEvent.layout;
				if (width > MINI_CARD_WIDTH - 2 * paddingMedium) {
					setForceLongTitle(true);
				}
			}}
		>
			{title}
		</Text>
	);
};

const largeTextSpacing = paddingLarge;
const offset = paddingLarge;
const textStyle: TextStyle = isWeb
	? {
			paddingRight: largeTextSpacing,
		}
	: {
			position: "absolute",
			paddingRight: largeTextSpacing,
		};
const MiniCardLongTitle = ({ title }: { title: string }) => {
	const [containerWidth, setContainerWidth] = useState(0);
	// We don't want the first text to start immediately at the left edge, so we offset it by a bit
	const progress = useSharedValue(0);
	const opacity = useSharedValue(0);

	const animatedStyle0 = useAnimatedStyle(
		() => ({
			opacity: opacity.value,
			transform: [
				{
					translateX: containerWidth + -3 * progress.value * containerWidth + offset,
				},
			],
		}),
		[containerWidth, progress, opacity],
	);
	const animatedStyle1 = useAnimatedStyle(
		() => ({
			opacity: opacity.value,
			transform: [
				{
					translateX:
						containerWidth +
						-3 * ((progress.value + 1 / 3) % 1) * containerWidth +
						offset,
				},
			],
		}),
		[containerWidth, progress, opacity],
	);
	const animatedStyle2 = useAnimatedStyle(
		() => ({
			opacity: opacity.value,
			transform: [
				{
					translateX:
						containerWidth +
						-3 * ((progress.value + 2 / 3) % 1) * containerWidth +
						offset,
				},
			],
		}),
		[containerWidth, progress, opacity],
	);
	useEffect(() => {
		if (containerWidth) {
			opacity.value = withTiming(1, {
				duration: 200,
			});
			progress.value = withRepeat(
				withTiming(1, {
					// Consistent speed
					duration: containerWidth * 120,
					easing: Easing.linear,
				}),
				-1,
			);
		}
	}, [containerWidth, progress, opacity]);

	return (
		<View
			style={{
				flex: 1,
				width: "100%",
				overflow: "hidden",
			}}
		>
			<Animated.View
				style={[
					{
						position: "absolute",
						left: 0,
					},
					animatedStyle0,
				]}
			>
				<Text
					xs
					semibold
					numberOfLines={1}
					onLayout={(event) => {
						const { width } = event.nativeEvent.layout;
						setContainerWidth(width);
					}}
					style={textStyle}
				>
					{title}
				</Text>
			</Animated.View>
			<Animated.View
				style={[
					{
						position: "absolute",
						left: 0,
					},
					animatedStyle1,
				]}
			>
				<Text xs semibold numberOfLines={1} style={textStyle}>
					{title}
				</Text>
			</Animated.View>
			<Animated.View
				style={[
					{
						position: "absolute",
						left: 0,
					},
					animatedStyle2,
				]}
			>
				<Text xs semibold numberOfLines={1} style={textStyle}>
					{title}
				</Text>
			</Animated.View>
		</View>
	);
};
export default memo(MiniCardTitle);
